import { useRouter } from '@tanstack/react-router'
import { Select } from 'antd'
import clsx from 'clsx'
import { useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaExchangeAlt,
  FaLayerGroup,
  FaPen,
  FaPlus,
  FaTrash,
  FaUnlink
} from 'react-icons/fa'

import {
  Button,
  TableActions,
  OverlayButton,
  OverlayDivider
} from '@/components/elements'
import {
  ACTIONS,
  DESCRIPTIONS,
  ERROR_MESSAGES,
  MENU,
  SUCCESS_MESSAGES
} from '@/constants/common/message.constants'
import { toastApiErrorMessage } from '@/features/shared/utils'
import {
  bulkDeleteShipmentOrders,
  bulkUpdateDeliveryStatus,
  bulkUpdateShipmentOrderDates,
  unbundleShipmentOrders,
  updateShipmentOrders
} from '@/features/shipment-orders/api'
import {
  BulkDeleteShipmentOrdersModal,
  ShipmentOrderBundleDateAlignModal,
  ShipmentOrderCreateModal
} from '@/features/shipment-orders/components'
import { useCreateShipmentBundle } from '@/features/shipment-orders/hooks'
// canBundleOrders / collectBundleDateOptions は useCreateShipmentBundle 側へ移した（#4705）
import { canDeleteOrders } from '@/features/shipment-orders/models'
import { useAntdModal } from '@/libs/antd/hooks'
import {
  type PageEntity,
  type ShipmentOrdersListExEntity,
  type UserMeExEntity
} from '@/libs/api'
import {
  type ShipmentDeliveryStatusType,
  SHIPMENT_DELIVERY_STATUS_TYPE,
  SHIPMENT_DELIVERY_STATUS_TYPE_LABEL
} from '@/libs/api/types'
import { useOptionalFormContext } from '@/libs/hooks'
import { safeFormatISO } from '@/libs/utils'

import { type ShipmentOrdersSearch } from '../../-models'
import { type ShipmentOrdersFormValues } from '../../-models/shipment-orders-form-values'
import { ShipmentOrdersSelectionSummary } from '../ShipmentOrdersSelectionSummary'

type Props = {
  tenantKey: string
  authUser: UserMeExEntity
  search: ShipmentOrdersSearch
  pagination: PageEntity | null
  onChangePage: (page: number, pageSize: number) => void
  selectedRowKeys: React.Key[]
  clearSelection: () => void
  selectedShipmentOrders: ShipmentOrdersListExEntity[]
  isEditing: boolean
  setIdEditing: React.Dispatch<React.SetStateAction<boolean>>
}

const deliveryStatusOptions = Object.entries(SHIPMENT_DELIVERY_STATUS_TYPE).map(
  ([, value]) => ({
    label: SHIPMENT_DELIVERY_STATUS_TYPE_LABEL[value],
    value
  })
)

export const ShipmentOrdersTableHeaderActions = ({
  tenantKey,
  authUser,
  search,
  pagination,
  onChangePage,
  selectedRowKeys,
  clearSelection,
  selectedShipmentOrders,
  isEditing,
  setIdEditing
}: Props) => {
  const form = useOptionalFormContext<ShipmentOrdersFormValues>()
  const handleSubmit = form?.handleSubmit
  const reset = form?.reset
  const errors = form?.formState.errors ?? {}
  const router = useRouter()
  const [modal, modalContext] = useAntdModal()
  const [bulkStatusUpdating, setBulkStatusUpdating] = useState(false)
  const [bundleUpdating, setBundleUpdating] = useState(false)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [changeDatesModalOpen, setChangeDatesModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const selectedStatusRef = useRef<ShipmentDeliveryStatusType | null>(null)

  // 作成モーダルの発送日初期値。検索条件の発送日From、無ければ当日。
  const createShippingDate =
    search.shippingDateFrom || safeFormatISO(new Date())

  // まとめられない選択（2件未満・配車済み混在・まとまり数超過・取消済み混在。
  // 判定規則は canBundleOrders 参照）のときはグレーアウトで残さずボタン自体を出さない
  // （「単一の依頼に戻す」と同じ方針。押す直前まで実行できないと気づけないため）。
  // 実行中 (bundling) は押せないだけでボタンは残す
  const {
    canBundle: canBundleShipmentOrders,
    bundling,
    dateAlignModalOpen,
    closeDateAlignModal,
    startBundleShipmentOrders,
    bundleShipmentOrders
  } = useCreateShipmentBundle({
    tenantKey,
    selectedShipmentOrders,
    onBundled: async () => {
      await router.invalidate()
      clearSelection()
    }
  })

  // 「単一の依頼に戻す」対象は、選択行のうちまとめキー（束ね）を持つ依頼だけ。
  // BE は渡した依頼を束ねの所属によらず配車から外すため、束ねに属さない依頼を混ぜて送ると
  // 無関係な配車まで解除してしまう
  const bundledSelection = useMemo(
    () =>
      selectedShipmentOrders.filter(
        (order) => typeof order.bundleId === 'number'
      ),
    [selectedShipmentOrders]
  )

  // 1つのまとめから外す操作に限定する（複数のまとめキーが混ざる選択では実行させない）。
  // 誤操作で複数のまとめが一度に全部消えるのを防ぐのが目的。
  // まとめキーを持たない依頼が1件でも混ざる選択でも出さない（戻す対象がない依頼を
  // 選んだまま実行でき、どれが戻るのか選択から読み取れないため）。
  // 条件を満たさない間はグレーアウトではなくボタン自体を出さない（押す直前まで
  // 押せないと気づけないため）
  const canRestoreToSingle =
    bundledSelection.length === selectedShipmentOrders.length &&
    new Set(bundledSelection.map((order) => order.bundleId)).size === 1

  // 日付の一括変更は配車前だけの導線（配車後は配車板の矢羽根編集モーダルが担う）。
  // 対象は選択した依頼そのもので、束ねへの所属は問わない（束ねの一部だけ・複数束ね混在も可。
  // 束ね内で日付が割れることは許容し、束ね時の整合は束ね作成時の日付統一モーダルが担う）。
  // 配車済みの依頼が混ざる選択では出さない（グレーアウトで残さない方針は他ボタンと同じ）
  const canBulkUpdateDates =
    selectedShipmentOrders.length > 0 &&
    selectedShipmentOrders.every((order) => order.dispatchDetails.length === 0)

  // VOC_PJ-445 #1864: 物理削除は未配車かつキャンセル以外のときだけ（判定は canDeleteOrders 参照）。
  // 配車済み・キャンセルが1件でも混ざる選択ではボタン自体を出さない
  // （グレーアウトで残さない方針は他ボタンと同じ）
  const canDeleteShipmentOrders = canDeleteOrders(selectedShipmentOrders)

  const onCancel = () => {
    setIdEditing(false)
    clearSelection()
    reset?.()
  }

  const onStartShipmentOrdersEdit = () => {
    setIdEditing(true)
  }

  const onSubmit = () => {
    if (!handleSubmit) {
      return
    }
    handleSubmit(async (formValue) => {
      try {
        // empty が入る可能性があるため排除する
        const formatedFormValue = formValue.shipmentOrders.filter(Boolean)

        await updateShipmentOrders({
          tenantKey,
          dto: { shipmentOrders: formatedFormValue }
        })

        await router.invalidate()
        toast.success(SUCCESS_MESSAGES.UPDATED_ORDER_NO)
      } catch (error) {
        toast.error(ERROR_MESSAGES.FAILED_TO_UPDATE_ORDER_NO)
      }

      setIdEditing(false)
      clearSelection()
      reset?.()
    })()
  }

  const onBulkStatusUpdate = () => {
    selectedStatusRef.current = null

    modal.confirm({
      title: MENU.BULK_UPDATING_STATUS,
      content: (
        <Select<ShipmentDeliveryStatusType>
          className="mt-2 w-full"
          placeholder={DESCRIPTIONS.SELECT_TARGET_STATUS}
          options={deliveryStatusOptions}
          onChange={(value) => {
            selectedStatusRef.current = value
          }}
        />
      ),
      okText: ACTIONS.UPDATE,
      cancelText: ACTIONS.CANCEL,
      onOk: async () => {
        const deliveryStatus = selectedStatusRef.current
        if (!deliveryStatus) {
          toast.error(DESCRIPTIONS.SELECT_STATUS)
          return Promise.reject()
        }
        setBulkStatusUpdating(true)
        try {
          const result = await bulkUpdateDeliveryStatus({
            tenantKey,
            ids: selectedRowKeys.map(Number),
            deliveryStatus
          })
          await router.invalidate()
          clearSelection()
          toast.success(
            SUCCESS_MESSAGES.SUCCESS_COUNT_UPDATED_STATUS_INTO(
              result.updatedCount,
              SHIPMENT_DELIVERY_STATUS_TYPE_LABEL[deliveryStatus]
            )
          )
        } catch {
          toast.error(ERROR_MESSAGES.FAILED_TO_UPDATE_STATUS)
        } finally {
          setBulkStatusUpdating(false)
        }
      }
    })
  }

  // 選択した依頼の発日・着日・請求計上日をまとめて変更する（配車前のみ）
  const bulkUpdateDates = async (dates: {
    shippingDate?: string
    deliveryDate?: string
    incomeRecordingDateHandling?: 'FOLLOW' | 'ALIGN' | 'KEEP'
    incomeRecordingDate?: string
  }) => {
    setBundleUpdating(true)
    try {
      await bulkUpdateShipmentOrderDates({
        tenantKey,
        shipmentOrderIds: selectedShipmentOrders.map((order) => order.id),
        ...dates
      })
      await router.invalidate()
      clearSelection()
      setChangeDatesModalOpen(false)
      toast.success(SUCCESS_MESSAGES.BULK_UPDATED_DATES)
    } catch (error) {
      toastApiErrorMessage(error, ERROR_MESSAGES.FAILED_TO_BULK_UPDATE_DATES)
    } finally {
      setBundleUpdating(false)
    }
  }

  // 選択した依頼をまとめから外して単一の依頼に戻す。
  // 束ねのメンバーと配車の中身は一致している必要があるため、外した依頼が配車済なら
  // 配車も解除される（BE 側で必ず外れる）。配車済を含むときだけ事前に確認する
  const onRestoreToSingleShipmentOrders = () => {
    if (bundledSelection.length === 0) {
      return
    }

    const restoreToSingle = async () => {
      setBundleUpdating(true)
      try {
        const result = await unbundleShipmentOrders({
          tenantKey,
          shipmentOrderIds: bundledSelection.map((order) => order.id)
        })
        await router.invalidate()
        clearSelection()
        toast.success(
          result.detachedFromDispatchCount > 0
            ? SUCCESS_MESSAGES.SEPARATED_FROM_BUNDLE_WITH_DISPATCH(
                result.detachedFromDispatchCount
              )
            : SUCCESS_MESSAGES.SEPARATED_FROM_BUNDLE
        )
      } catch (error) {
        toastApiErrorMessage(
          error,
          ERROR_MESSAGES.FAILED_TO_SEPARATE_FROM_BUNDLE
        )
      } finally {
        setBundleUpdating(false)
      }
    }

    const dispatchedCount = bundledSelection.filter(
      (order) => order.dispatchDetails.length > 0
    ).length

    if (dispatchedCount === 0) {
      void restoreToSingle()
      return
    }

    modal.confirm({
      title: ACTIONS.RESTORE_TO_SINGLE_SHIPMENT_ORDER,
      content:
        DESCRIPTIONS.SEPARATE_DISPATCHED_FROM_BUNDLE_NOTICE(dispatchedCount),
      okText: ACTIONS.RESTORE_TO_SINGLE_SHIPMENT_ORDER,
      okButtonProps: { danger: true },
      onOk: restoreToSingle
    })
  }

  // VOC_PJ-445 #1864: 選択した配送依頼をまとめて物理削除する。
  // 1件ずつ削除APIを叩かず専用APIに任せるのは、束ねからの複数削除でまとめキーの解消が
  // 途中で止まったり、同一グループの削除が二重に走ったりするのを防ぐため（BE で1トランザクション）
  const onBulkDeleteShipmentOrders = async () => {
    setDeleting(true)
    try {
      const result = await bulkDeleteShipmentOrders({
        tenantKey,
        ids: selectedShipmentOrders.map((order) => order.id)
      })
      await router.invalidate()
      clearSelection()
      setDeleteModalOpen(false)
      toast.success(
        SUCCESS_MESSAGES.DELETED_SHIPMENT_ORDERS(result.deletedCount)
      )
    } catch (error) {
      toastApiErrorMessage(
        error,
        ERROR_MESSAGES.FAILED_TO_DELETE_SHIPMENT_ORDERS
      )
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <TableActions
        // 選択中は青いオーバーレイ(一括操作メニュー)をスクロール追従させる。
        // テーブルの列ヘッダはこのバーの下(offsetHeader)に貼り付く。
        className={clsx([
          'align-top',
          selectedRowKeys.length && 'sticky top-0 z-10'
        ])}
        pageEntity={pagination}
        showTotal
        onChangePage={onChangePage}
        actions={
          <Button
            type="default"
            icon={<FaPlus />}
            onClick={() => setCreateModalOpen(true)}
          >
            {ACTIONS.CREATE_SHIPMENT_ORDER}
          </Button>
        }
        extra={
          <ShipmentOrdersSelectionSummary
            shipmentOrders={selectedShipmentOrders}
          />
        }
        overlayActions={
          <>
            <OverlayButton onClick={onCancel}>{ACTIONS.CANCEL}</OverlayButton>
            {isEditing ? (
              <>
                <OverlayDivider />
                <OverlayButton
                  icon={<FaCheckCircle />}
                  onClick={onSubmit}
                  // 入力されていないもの(error)が存在するときはdisabled
                  disabled={Object.keys(errors).length > 0}
                >
                  {ACTIONS.SAVE_EDIT}
                </OverlayButton>
              </>
            ) : (
              <>
                {/* 束ね系のボタンが両方消える選択では区切りも出さない */}
                {(canBundleShipmentOrders || canRestoreToSingle) && (
                  <OverlayDivider />
                )}
                {/* まとめられる選択のときにしか出さない。グレーアウトで残すと
                    押す直前まで実行できないと気づけないため */}
                {canBundleShipmentOrders && (
                  <OverlayButton
                    icon={<FaLayerGroup />}
                    onClick={startBundleShipmentOrders}
                    disabled={bundling || bundleUpdating}
                  >
                    {ACTIONS.BUNDLE_SHIPMENT_ORDERS}
                  </OverlayButton>
                )}
                {/* 1つのまとめの中だけを選んでいるときにしか出さない。グレーアウトで
                    残すと押す直前まで実行できないと気づけないため */}
                {canRestoreToSingle && (
                  <OverlayButton
                    icon={<FaUnlink />}
                    onClick={onRestoreToSingleShipmentOrders}
                    disabled={bundleUpdating || bundling}
                  >
                    {ACTIONS.RESTORE_TO_SINGLE_SHIPMENT_ORDER}
                  </OverlayButton>
                )}
                <OverlayDivider />
                {/* 未配車の依頼を選んでいるときにしか出さない（配車後の
                    日付変更は配車板の矢羽根編集モーダルが担う） */}
                {canBulkUpdateDates && (
                  <OverlayButton
                    icon={<FaCalendarAlt />}
                    onClick={() => setChangeDatesModalOpen(true)}
                    disabled={bundleUpdating || bundling}
                  >
                    {ACTIONS.BULK_UPDATE_DATES}
                  </OverlayButton>
                )}
                <OverlayButton
                  icon={<FaExchangeAlt />}
                  onClick={onBulkStatusUpdate}
                  disabled={bulkStatusUpdating}
                >
                  {ACTIONS.UPDATE_STATUS}
                </OverlayButton>
                <OverlayDivider />
                <OverlayButton
                  icon={<FaPen />}
                  onClick={onStartShipmentOrdersEdit}
                >
                  {ACTIONS.EDIT_ORDER_NO}
                </OverlayButton>
                {/* 物理削除は元に戻せないため、編集系から区切って最後尾に置く。
                    未配車の依頼を選んでいるときにしか出さない。
                    オーバーレイバーのボタンは白文字固定（hover でも !text-white に戻る）なので
                    色では警告せず、確認モーダル側の danger ボタンで念を押す */}
                {canDeleteShipmentOrders && (
                  <>
                    <OverlayDivider />
                    <OverlayButton
                      icon={<FaTrash />}
                      onClick={() => setDeleteModalOpen(true)}
                      disabled={deleting}
                    >
                      {ACTIONS.DELETE}
                    </OverlayButton>
                  </>
                )}
              </>
            )}
          </>
        }
        selectedLength={selectedRowKeys.length}
      />
      {modalContext}
      <ShipmentOrderCreateModal
        authUser={authUser}
        tenantKey={tenantKey}
        shippingDate={createShippingDate}
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={() => router.invalidate()}
      />
      {dateAlignModalOpen && (
        <ShipmentOrderBundleDateAlignModal
          orders={selectedShipmentOrders}
          bundling={bundling}
          onCancel={closeDateAlignModal}
          onConfirm={bundleShipmentOrders}
        />
      )}
      {changeDatesModalOpen && (
        <ShipmentOrderBundleDateAlignModal
          variant="update"
          orders={selectedShipmentOrders}
          bundling={bundleUpdating}
          onCancel={() => setChangeDatesModalOpen(false)}
          onConfirm={bulkUpdateDates}
        />
      )}
      {deleteModalOpen && (
        <BulkDeleteShipmentOrdersModal
          orders={selectedShipmentOrders}
          deleting={deleting}
          onCancel={() => setDeleteModalOpen(false)}
          onConfirm={() => void onBulkDeleteShipmentOrders()}
        />
      )}
    </>
  )
}
